import {useSetCustomBreadcrumb} from '@/hooks/useBreadcrumbs';
import {dirtyValues} from '@/lib/react-hook-form-helpers';
import {BaseKey} from '@refinedev/core';
import {useForm, UseFormProps, UseFormReturnType} from '@refinedev/react-hook-form';
import {defaultMutationOptions, defaultQueryOptions} from 'pages/_utils';
import {useCallback, useMemo} from 'react';

export type UseEditFormProps = {
  resource?: string; // if undefined, it will be taken from formProps.refineCoreProps.resource
  id?: BaseKey;
  formProps?: UseFormProps;
  transformBeforeSave?: (data: any) => any;
  submitDirtyFieldsOnly?: boolean;
  crumbSetter?: (data) => string;
};

export const useEditForm: (props: UseEditFormProps) => UseFormReturnType = (
  props: UseEditFormProps,
) => {
  const {
    resource,
    id,
    formProps,
    transformBeforeSave,
    submitDirtyFieldsOnly = true,
    crumbSetter = data => data?.name,
  } = props;
  
  const useFormResults = useForm({
    ...formProps,
    refineCoreProps: {
      ...props.formProps?.refineCoreProps,
      resource: resource || formProps?.refineCoreProps?.resource,
      id: id || formProps?.refineCoreProps?.id,
      action: 'edit',
      updateMutationOptions: defaultMutationOptions,
      queryOptions: defaultQueryOptions,
    },
  });

  const {
    refineCore: {onFinish},
  } = useFormResults;

  const onFinishHandler = useCallback(
    async function (data) {
      const dirtyOnly = submitDirtyFieldsOnly
        ? dirtyValues(useFormResults.formState.dirtyFields, data)
        : data;
      const transformed = transformBeforeSave?.(dirtyOnly) || dirtyOnly;
      await onFinish(transformed);
    },
    [useFormResults.formState.dirtyFields, transformBeforeSave, onFinish, submitDirtyFieldsOnly],
  );

  useSetCustomBreadcrumb(crumbSetter?.(useFormResults?.refineCore?.query?.data?.data));
  // if (crumbSetter && useFormResults?.refineCore?.query?.data?.data)
  //   setCustomBreadcrumb(crumbSetter(useFormResults?.refineCore?.query?.data?.data));

  return useMemo(
    () => ({
      ...useFormResults,
      refineCore: {
        ...useFormResults.refineCore,
        onFinish: onFinishHandler,
      },
    }),
    [useFormResults, useFormResults.refineCore, onFinishHandler],
  );
};
